/**
 * MetricsFirstPaymentCohortsPage — 数据中台 · 付费 cohort（ADM-MTR-S014）
 *
 * 以激活为 cohort，按分组维度（默认语言）看激活用户首次付费落在激活后第几个 UTC 自然日：
 * 激活数 + D0 / D1 / D2 / D3-6 / D7-13 / D14+ 转化率（桶首付人数 ÷ 激活数）+ 累计付费率；
 * 首行为全窗口汇总，可排序、导出 CSV。观察未满的桶标 *（桶人数是下限）。
 * 分组含语言时，各行可展开到地区：展开时以「当前分组 + country」再查一次，子行按父行分组键挂到父行下。
 * 数据走 useMetricsFirstPaymentCohorts（GET /api/v1/metrics/first-payment-cohorts）；应用维度跟随顶栏 App 选择器。
 */

import {
  FIRST_PAYMENT_COHORT_BUCKETS,
  FIRST_PAYMENT_COHORT_MAX_DAYS,
  FIRST_PAYMENT_COHORT_MAX_ROWS,
  type FirstPaymentCohortBucket,
  type FirstPaymentCohortDimension,
} from '@novel-admin/shared';
import { Fragment, useMemo, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import type {
  FirstPaymentCohortMetricsView,
  FirstPaymentCohortRowView,
  FirstPaymentCohortViewData,
} from '~/data/adapters/metrics-first-payment-cohorts-adapter';
import { useMetricsFirstPaymentCohorts } from '~/data/queries';
import { fmtCountry, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { ExpandToggleButton } from '~/ui/ExpandToggleButton';
import { FilterButton } from '~/ui/FilterButton';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import {
  ChipRow,
  ToggleChip,
  daysBetween,
  downloadCsvFile,
  toDay,
} from './conversion-dashboard-shared';
import { MetricsDataTableHeading } from './metrics-data-explanation';
import { Cal, EmptyBlock, StateGate, type StateGateMode } from './metrics-shared';

type ValueMode = 'rate' | 'count';

/** 可选分组维度；地区不作为芯片，只用于语言行展开 */
const DIMENSIONS: { key: FirstPaymentCohortDimension; label: string }[] = [
  { key: 'language', label: '语言' },
  { key: 'date', label: '激活日期' },
  { key: 'app', label: '应用' },
  { key: 'platform', label: '终端' },
  { key: 'channel', label: '渠道' },
];

const PLATFORM_LABEL: Record<string, string> = { IOS: 'iOS', ANDROID: 'Android', WEB: 'Web' };
const CHANNEL_TYPE_LABEL: Record<string, string> = {
  NON_ORGANIC: '投放',
  ORGANIC: '自然量',
  ORGANIC_OR_UNKNOWN: '未知',
};

/** 默认近一年（含今天）：生命周期口径，窗口上限 366 天 */
function defaultCohortRange(): [Date, Date] {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 364);
  return [start, end];
}

const pct = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(2)}%`);

function bucketText(
  metrics: FirstPaymentCohortMetricsView,
  bucket: FirstPaymentCohortBucket,
  mode: ValueMode,
): string {
  const cell = metrics.buckets[bucket];
  return mode === 'rate' ? pct(cell.rate) : cell.count.toLocaleString();
}

function totalText(metrics: FirstPaymentCohortMetricsView, mode: ValueMode): string {
  return mode === 'rate' ? pct(metrics.payerRate) : metrics.payers.toLocaleString();
}

function dimensionLabel(
  dimension: FirstPaymentCohortDimension,
  value: string | null | undefined,
  data: FirstPaymentCohortViewData,
): string {
  if (value === null || value === undefined || value === '')
    return dimension === 'country' ? '未知地区' : '未知';
  switch (dimension) {
    case 'country':
      return fmtCountry(value);
    case 'language':
      return fmtLang(value);
    case 'app':
      return data.options.apps.find((app) => app.id === value)?.name ?? value;
    case 'platform':
      return PLATFORM_LABEL[value] ?? value;
    case 'channel':
      return value === 'organic' ? '自然量' : value;
    default:
      return value;
  }
}

/** 子行（地区）按父行分组键归组；键与 adapter 的 row.key 同构（父行维度值数组的 JSON） */
function groupChildrenByParent(
  parentDims: FirstPaymentCohortDimension[],
  children: FirstPaymentCohortRowView[],
): Map<string, FirstPaymentCohortRowView[]> {
  const map = new Map<string, FirstPaymentCohortRowView[]>();
  for (const child of children) {
    const parentKey = JSON.stringify(parentDims.map((d) => child.dims[d] ?? null));
    const list = map.get(parentKey) ?? [];
    list.push(child);
    map.set(parentKey, list);
  }
  for (const list of map.values()) list.sort((a, b) => b.activations - a.activations);
  return map;
}

function downloadCsv(
  data: FirstPaymentCohortViewData,
  rows: FirstPaymentCohortRowView[],
  mode: ValueMode,
  expandedChildren: Map<string, FirstPaymentCohortRowView[]> | null,
): void {
  const suffix = mode === 'rate' ? '转化率%' : '人数';
  // 有展开的地区行时多一列「地区」：父行填「全部」，子行填地区名，与表格所见一致
  const withCountry = expandedChildren !== null && expandedChildren.size > 0;
  const header = [
    ...data.groupBy.map((d) => DIMENSIONS.find((x) => x.key === d)?.label ?? d),
    ...(withCountry ? ['地区'] : []),
    '激活数',
    ...FIRST_PAYMENT_COHORT_BUCKETS.map((b) => `${b.label}${suffix}`),
    `累计${suffix}`,
  ];
  const line = (dims: string[], view: FirstPaymentCohortMetricsView) => [
    ...dims,
    String(view.activations),
    // 观察未满的格子带 *，与表格一致：导出后下限值不会被当成终值
    ...FIRST_PAYMENT_COHORT_BUCKETS.map(
      (b) => `${bucketText(view, b.key, mode)}${view.buckets[b.key].immature ? '*' : ''}`,
    ),
    totalText(view, mode),
  ];
  downloadCsvFile(`first-payment-cohorts-${data.window.from}_${data.window.to}.csv`, [
    header,
    line(
      [
        ...data.groupBy.map((_, index) => (index === 0 ? '汇总' : '')),
        ...(withCountry ? [''] : []),
      ],
      data.summary,
    ),
    ...rows.flatMap((row) => {
      const parentDims = data.groupBy.map((d) => dimensionLabel(d, row.dims[d], data));
      const parent = line([...parentDims, ...(withCountry ? ['全部'] : [])], row);
      const children = expandedChildren?.get(row.key) ?? [];
      return [
        parent,
        ...children.map((child) =>
          line([...parentDims, dimensionLabel('country', child.dims.country, data)], child),
        ),
      ];
    }),
  ]);
}

export default function MetricsFirstPaymentCohortsPage() {
  const appId = useAppScope((s) => s.appId);
  const [range, setRange] = useState<[Date, Date]>(defaultCohortRange);
  const [channelTypes, setChannelTypes] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [minActivationsInput, setMinActivationsInput] = useState('');
  const [dims, setDims] = useState<FirstPaymentCohortDimension[]>(['language']);
  const [mode, setMode] = useState<ValueMode>('rate');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  /** 已展开到地区的行（键 = 行的分组键） */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const from = toDay(range[0]);
  const to = toDay(range[1]);
  const spanDays = daysBetween(from, to).length;
  const rangeBlocked = spanDays > FIRST_PAYMENT_COHORT_MAX_DAYS;
  const minActivations = /^\d+$/.test(minActivationsInput.trim())
    ? Number(minActivationsInput.trim())
    : null;

  const queryBase = {
    from,
    to,
    appIds: appId === 'all' ? [] : [appId],
    channelTypes,
    languages,
    minActivations,
  };
  const {
    data,
    isLoading,
    isFetching,
    isError,
    isPlaceholderData: parentIsPlaceholder,
    refetch,
  } = useMetricsFirstPaymentCohorts({
    ...queryBase,
    groupBy: dims,
    enabled: !rangeBlocked,
  });
  // 语言行可展开到地区；有可见行展开时才查「当前分组 + country」，一次拿回所有父行的地区子行。
  // 激活数门槛只过滤父行（dev-docs AD §17 / ADM-MTR-S014）：子行不带门槛，子行之和 = 父行
  const expandable = dims.includes('language');
  const anyExpanded = expandable && (data?.rows ?? []).some((row) => expanded.has(row.key));
  const countryQuery = useMetricsFirstPaymentCohorts({
    ...queryBase,
    minActivations: null,
    groupBy: [...dims, 'country'],
    enabled: !rangeBlocked && anyExpanded,
  });
  // 父行与子行都必须是当前参数的结果：任一方还是上一组筛选 / 分组的占位，挂起来就会对不上
  const countryFresh =
    anyExpanded &&
    countryQuery.data !== undefined &&
    !countryQuery.isPlaceholderData &&
    !parentIsPlaceholder;
  const childrenByParent = useMemo(
    () =>
      countryFresh && countryQuery.data
        ? groupChildrenByParent(dims, countryQuery.data.rows)
        : null,
    [countryFresh, countryQuery.data, dims],
  );
  const toggleRow = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const activeFilterCount =
    channelTypes.length + languages.length + (minActivations !== null ? 1 : 0);
  const resetFilters = () => {
    setChannelTypes([]);
    setLanguages([]);
    setMinActivationsInput('');
  };

  const sortedRows = useMemo(() => {
    if (!data) return [];
    if (!sort) return data.rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    // 维度按展示文本排序；指标列按当前显示的值排（转化率 / 人数跨行不同序，分母为 0 的行排最后）
    const sortValue = (row: FirstPaymentCohortRowView): number | string | null => {
      if (sort.key.startsWith('dim:')) {
        const dimension = sort.key.slice(4) as FirstPaymentCohortDimension;
        const raw = row.dims[dimension];
        return raw === null || raw === undefined ? null : dimensionLabel(dimension, raw, data);
      }
      if (sort.key === 'activations') return row.activations;
      if (sort.key === 'total') return mode === 'rate' ? row.payerRate : row.payers;
      const cell = row.buckets[sort.key as FirstPaymentCohortBucket];
      return mode === 'rate' ? cell.rate : cell.count;
    };
    return [...data.rows].sort((left, right) => {
      const a = sortValue(left);
      const b = sortValue(right);
      if (a === b) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      const order =
        typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : a < b ? -1 : 1;
      return order * factor;
    });
  }, [data, sort, mode]);

  const toggleSort = (key: string) =>
    setSort((current) =>
      current?.key !== key
        ? { key, dir: 'desc' }
        : current.dir === 'desc'
          ? { key, dir: 'asc' }
          : null,
    );

  const gate: StateGateMode = rangeBlocked
    ? 'normal'
    : isLoading
      ? 'loading'
      : isError || !data
        ? 'error'
        : 'normal';

  // 已选中但不在当前候选里的值也要能显示 / 取消（候选随时间窗变化）
  const languageOptions = [...new Set([...(data?.options.languages ?? []), ...languages])];

  return (
    <div className="page" data-screen-label="付费 cohort">
      <div className="page-header">
        <div>
          <h1 className="page-title">付费 cohort</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            激活用户首次付费天数分布 · 按激活 UTC 日
            <Cal
              t="生命周期"
              title="激活 = 首次归因安装（含自然量）；首付 = 激活后最早一笔 Production 到账，天数按 UTC 自然日差；观察至当前时刻。语言优先取投放书语言，无投放书时取用户最近请求语言，再取注册语言"
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          // 重取期间 data 仍是上一组筛选的占位数据，禁止导出以免导出与当前筛选不符
          disabled={!data || isFetching || (anyExpanded && countryQuery.isFetching) || rangeBlocked}
          onClick={() => {
            if (!data) return;
            const visibleChildren = childrenByParent
              ? new Map([...childrenByParent].filter(([key]) => expanded.has(key)))
              : null;
            downloadCsv(data, sortedRows, mode, visibleChildren);
            toast('已导出 CSV', { tone: 'pos', icon: 'IconDownload' });
          }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <div
        className="card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
          <Icons.IconFilter size={13} />
          筛选
        </span>
        <DateRangeButton value={range} onChange={setRange} />
        <FilterButton
          label="渠道类型"
          options={Object.entries(CHANNEL_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
          value={channelTypes}
          onChange={setChannelTypes}
        />
        <FilterButton
          label="语言"
          options={languageOptions.map((v) => ({ value: v, label: fmtLang(v) }))}
          value={languages}
          onChange={setLanguages}
        />
        <label
          className="hstack"
          style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}
          title="只显示激活数不低于该值的分组行（不影响汇总行）"
        >
          激活数 ≥
          <input
            className="input mono"
            inputMode="numeric"
            placeholder="不限"
            value={minActivationsInput}
            // 8 位封顶：接口上限 1 亿，超出会被 422 拒绝、整页报加载失败
            onChange={(e) =>
              setMinActivationsInput(e.target.value.replace(/[^\d]/g, '').slice(0, 8))
            }
            style={{ width: 80 }}
          />
        </label>
        {activeFilterCount > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
            <Icons.IconX size={11} />
            重置 {activeFilterCount}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {isFetching && !isLoading && (
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>更新中…</span>
        )}
      </div>

      <div className="card vstack" style={{ padding: '12px 14px', gap: 8 }}>
        <ChipRow label="分组">
          {DIMENSIONS.map((d) => (
            <ToggleChip
              key={d.key}
              label={d.label}
              on={dims.includes(d.key)}
              onClick={() => {
                // 分组变了，原有行键失效，展开状态一并清掉
                setExpanded(new Set());
                setDims((current) =>
                  current.includes(d.key)
                    ? // 至少保留一个分组维度：空 group_by 在服务端回落为按语言，芯片状态会与表格不一致
                      current.length === 1
                      ? current
                      : current.filter((x) => x !== d.key)
                    : DIMENSIONS.map((x) => x.key).filter(
                        (x) => x === d.key || current.includes(x),
                      ),
                );
              }}
            />
          ))}
        </ChipRow>
        <ChipRow label="显示">
          <ToggleChip label="转化率" on={mode === 'rate'} onClick={() => setMode('rate')} />
          <ToggleChip label="人数" on={mode === 'count'} onClick={() => setMode('count')} />
          {expandable && (
            <>
              <div style={{ flex: 1 }} />
              <div className="seg">
                <button
                  type="button"
                  onClick={() => setExpanded(new Set((data?.rows ?? []).map((row) => row.key)))}
                >
                  全部展开
                </button>
                <button type="button" onClick={() => setExpanded(new Set())}>
                  全部折叠
                </button>
              </div>
            </>
          )}
        </ChipRow>
      </div>

      <StateGate
        mode={gate}
        skeleton={<TableSkeleton rows={10} cols={9} />}
        errorNote="GET /api/v1/metrics/first-payment-cohorts 加载失败 · 请重试"
        onRetry={refetch}
      >
        {rangeBlocked && (
          <EmptyBlock
            title={`时间窗最多 ${FIRST_PAYMENT_COHORT_MAX_DAYS} 天`}
            note={`当前选择了 ${spanDays} 天，请缩短日期范围`}
          />
        )}
        {!rangeBlocked && data && (
          <CohortTable
            data={data}
            rows={sortedRows}
            mode={mode}
            sort={sort}
            onSort={toggleSort}
            expansion={
              expandable
                ? {
                    expanded,
                    onToggle: toggleRow,
                    childrenByParent,
                    // 非新鲜且未报错一律按加载中（含离线暂停：此时 isFetching 为 false）
                    childrenLoading: !countryFresh && !countryQuery.isError,
                    childrenError: countryQuery.isError,
                    childrenTruncated: countryFresh && (countryQuery.data?.truncated ?? false),
                  }
                : null
            }
          />
        )}
      </StateGate>
    </div>
  );
}

function BucketCell({
  metrics,
  bucket,
  mode,
}: {
  metrics: FirstPaymentCohortMetricsView;
  bucket: FirstPaymentCohortBucket;
  mode: ValueMode;
}) {
  const cell = metrics.buckets[bucket];
  return (
    <td
      className="num mono"
      title={
        cell.immature
          ? `观察未满：${cell.matured.toLocaleString()} / ${metrics.activations.toLocaleString()} 激活已过完该桶，数值为下限`
          : `${cell.count.toLocaleString()} 人首付落在该桶`
      }
    >
      {bucketText(metrics, bucket, mode)}
      {cell.immature && <span style={{ color: 'var(--fg-faint)' }}>*</span>}
    </td>
  );
}

type RowExpansion = {
  expanded: Set<string>;
  onToggle: (key: string) => void;
  childrenByParent: Map<string, FirstPaymentCohortRowView[]> | null;
  childrenLoading: boolean;
  childrenError: boolean;
  /** 地区查询命中行数上限：部分父行的地区子行不全 */
  childrenTruncated: boolean;
};

function CohortTable({
  data,
  rows,
  mode,
  sort,
  onSort,
  expansion,
}: {
  data: FirstPaymentCohortViewData;
  rows: FirstPaymentCohortRowView[];
  mode: ValueMode;
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
  /** null = 当前分组不含语言，行不可展开 */
  expansion: RowExpansion | null;
}) {
  const metricColumnCount = FIRST_PAYMENT_COHORT_BUCKETS.length + 2;
  const arrow = (key: string) => (sort?.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');
  const suffix = mode === 'rate' ? '转化率' : '人数';
  const metricHeader = (key: string, label: string, tip: string) => (
    <th
      key={key}
      className="num"
      title={tip}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      onClick={() => onSort(key)}
    >
      {label}
      {arrow(key)}
    </th>
  );
  const metricCells = (metrics: FirstPaymentCohortMetricsView) => (
    <>
      <td className="num mono">{metrics.activations.toLocaleString()}</td>
      {FIRST_PAYMENT_COHORT_BUCKETS.map((b) => (
        <BucketCell key={b.key} metrics={metrics} bucket={b.key} mode={mode} />
      ))}
      <td className="num mono" style={{ fontWeight: 600 }}>
        {totalText(metrics, mode)}
      </td>
    </>
  );
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <MetricsDataTableHeading title="首次付费时间分布" report="first-payment-cohorts" />
      {data.truncated && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12,
            color: 'var(--fg-muted)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          分组行超过上限，仅展示前 {rows.length.toLocaleString()} 行；请减少分组维度或缩小时间窗。
        </div>
      )}
      {expansion?.childrenTruncated && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12,
            color: 'var(--warn)',
            background: 'var(--warn-soft)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          地区行超过上限，部分父行的地区不全（按激活数保留前{' '}
          {FIRST_PAYMENT_COHORT_MAX_ROWS.toLocaleString()} 组）；请减少分组维度或缩小时间窗。
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 720 }}>
          <thead>
            <tr>
              {data.groupBy.map((d) => (
                <th
                  key={d}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => onSort(`dim:${d}`)}
                >
                  {DIMENSIONS.find((x) => x.key === d)?.label}
                  {arrow(`dim:${d}`)}
                </th>
              ))}
              {metricHeader('activations', '激活数', '窗口内激活的用户（App × 账号去重）')}
              {FIRST_PAYMENT_COHORT_BUCKETS.map((b) =>
                metricHeader(
                  b.key,
                  `${b.label}${suffix}`,
                  `首付落在激活后第 ${b.label.slice(1)} 天（UTC 自然日）的用户 ÷ 激活数`,
                ),
              )}
              {metricHeader('total', `累计${suffix}`, '有过首付的用户 ÷ 激活数（各桶之和）')}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600, background: 'var(--bg-subtle)' }}>
              {data.groupBy.map((d, index) => (
                <td key={d}>{index === 0 ? '汇总' : ''}</td>
              ))}
              {metricCells(data.summary)}
            </tr>
            {rows.map((row) => {
              const isOpen = expansion?.expanded.has(row.key) ?? false;
              const children = isOpen ? expansion?.childrenByParent?.get(row.key) : undefined;
              return (
                <Fragment key={row.key}>
                  <tr>
                    {data.groupBy.map((d, index) => (
                      <td
                        key={d}
                        className={d === 'date' ? 'mono' : undefined}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {index === 0 && expansion ? (
                          <span className="hstack" style={{ gap: 4 }}>
                            <ExpandToggleButton
                              size={22}
                              expanded={isOpen}
                              onToggle={() => expansion.onToggle(row.key)}
                              label={`${isOpen ? '折叠' : '展开'}${dimensionLabel(d, row.dims[d], data)}的地区`}
                            />
                            {dimensionLabel(d, row.dims[d], data)}
                          </span>
                        ) : (
                          dimensionLabel(d, row.dims[d], data)
                        )}
                      </td>
                    ))}
                    {metricCells(row)}
                  </tr>
                  {isOpen && !children && (
                    <tr>
                      <td
                        colSpan={data.groupBy.length + metricColumnCount}
                        style={{ paddingLeft: 40, fontSize: 12, color: 'var(--fg-muted)' }}
                      >
                        {expansion?.childrenError
                          ? '地区数据加载失败，请折叠后重试'
                          : expansion?.childrenLoading
                            ? '正在加载地区…'
                            : '该行没有地区数据'}
                      </td>
                    </tr>
                  )}
                  {children?.map((child) => (
                    <tr key={child.key} style={{ background: 'var(--bg-subtle)' }}>
                      {data.groupBy.map((d, index) => (
                        <td
                          key={d}
                          style={{
                            whiteSpace: 'nowrap',
                            paddingLeft: index === 0 ? 40 : undefined,
                            color: 'var(--fg-muted)',
                          }}
                        >
                          {index === 0 ? dimensionLabel('country', child.dims.country, data) : ''}
                        </td>
                      ))}
                      {metricCells(child)}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      {rows.length === 0 && (
        <EmptyBlock title="当前筛选下没有分组数据" note="换个时间窗、放宽筛选或调低激活数门槛" />
      )}
      <div
        style={{
          padding: '8px 14px',
          fontSize: 11.5,
          color: 'var(--fg-faint)',
          borderTop: '1px solid var(--border)',
        }}
      >
        *
        观察未满：该组有激活用户还没过完这个桶的全部天数，数值是下限。每个用户只计入首付所在的一个桶，
        付费来自 Production 到账结算，观察至{' '}
        <span className="mono">{data.observedUntil.slice(0, 16).replace('T', ' ')} UTC</span>。
      </div>
    </div>
  );
}
